import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FieldError } from "@/components/States";

export const lockImageUrl = (l) => `${process.env.REACT_APP_BACKEND_URL}/api/mortise-locks/${l.id}/image?v=${encodeURIComponent(l.image_version || "")}`;

const BITS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const blank = { brand: "", model: "", colour: "", identification: "", drill_point: "", drill_bit: "" };

const Suggest = ({ id, label, value, onChange, options, error, testId }) => (
  <div>
    <Label htmlFor={id}>{label} *</Label>
    <Input id={id} list={`${id}-list`} value={value} onChange={(e) => onChange(e.target.value)} maxLength={120} className="mt-1.5" data-testid={testId} />
    <datalist id={`${id}-list`}>{options.map((o) => <option key={o} value={o} />)}</datalist>
    <FieldError message={error} testId={`${testId}-error`} />
  </div>
);

const ImagePicker = ({ preview, onPick, onClear }) => (
  <div>
    <Label>Image</Label>
    <div className="mt-1.5 flex items-center gap-3">
      <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
        {preview ? <img src={preview} alt="Lock preview" className="h-full w-full object-cover" data-testid="lock-form-image-preview" /> : <ImagePlus className="h-7 w-7 text-slate-400" />}
      </div>
      <label className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
        {preview ? "Change image" : "Choose image"}
        <input type="file" accept="image/png,image/jpeg,image/webp,image/heic" className="sr-only" onChange={(e) => onPick(e.target.files?.[0])} data-testid="lock-form-image-input" />
      </label>
      {preview && <Button type="button" size="icon" variant="ghost" onClick={onClear} aria-label="Remove image" data-testid="lock-form-image-remove"><X className="h-4 w-4" /></Button>}
    </div>
  </div>
);

export const MortiseLockDialog = ({ open, onOpenChange, lock, locks, onSaved }) => {
  const [f, setF] = useState(blank);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [removed, setRemoved] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const brands = useMemo(() => [...new Set(locks.map((l) => l.brand))].sort(), [locks]);
  const colours = useMemo(() => [...new Set(locks.map((l) => l.colour))].sort(), [locks]);

  useEffect(() => {
    if (open) {
      setF(lock ? { brand: lock.brand, model: lock.model, colour: lock.colour, identification: lock.identification || "", drill_point: lock.drill_point, drill_bit: String(lock.drill_bit) } : blank);
      setFile(null);
      setRemoved(false);
      setErrors({});
      setPreview(lock?.has_image ? lockImageUrl(lock) : null);
    }
  }, [open, lock]);

  const pick = (fl) => {
    if (!fl) return;
    if (fl.size > 8 * 1024 * 1024) {
      toast.error("Image is too large. Use a photo under 8 MB.");
      return;
    }
    setFile(fl);
    setRemoved(false);
    setPreview(URL.createObjectURL(fl));
  };

  const save = async (e) => {
    e.preventDefault();
    const errs = {};
    ["brand", "model", "colour"].forEach((k) => { if (!f[k].trim()) errs[k] = "Required"; });
    if (!f.drill_bit) errs.drill_bit = "Choose a drill bit";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const body = new FormData();
    Object.entries(f).forEach(([k, v]) => body.append(k, v.trim()));
    if (file) body.append("image", file);
    if (lock && removed) body.append("remove_image", "true");
    setBusy(true);
    try {
      const res = lock ? await api.put(`/mortise-locks/${lock.id}`, body) : await api.post("/mortise-locks", body);
      toast.success(lock ? "Lock updated" : "Lock added");
      onSaved(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="lock-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{lock ? "Edit mortise lock" : "Add mortise lock"}</DialogTitle></DialogHeader>
        <form onSubmit={save} noValidate className="space-y-4" data-testid="lock-form">
          <ImagePicker preview={preview} onPick={pick} onClear={() => { setFile(null); setPreview(null); setRemoved(true); }} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Suggest id="lf-brand" label="Brand" value={f.brand} onChange={set("brand")} options={brands} error={errors.brand} testId="lock-form-brand-input" />
            <Suggest id="lf-colour" label="Colour" value={f.colour} onChange={set("colour")} options={colours} error={errors.colour} testId="lock-form-colour-input" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="lf-model">Model *</Label>
              <Input id="lf-model" value={f.model} onChange={(e) => set("model")(e.target.value)} maxLength={120} className="mt-1.5" data-testid="lock-form-model-input" />
              <FieldError message={errors.model} testId="lock-form-model-input-error" />
            </div>
            <div>
              <Label>Drill bit *</Label>
              <Select value={f.drill_bit} onValueChange={set("drill_bit")}>
                <SelectTrigger className="mt-1.5" data-testid="lock-form-drill-bit-select"><SelectValue placeholder="Choose 1–12" /></SelectTrigger>
                <SelectContent>{BITS.map((b) => <SelectItem key={b} value={b} data-testid={`lock-form-drill-bit-${b}`}>{b}</SelectItem>)}</SelectContent>
              </Select>
              <FieldError message={errors.drill_bit} testId="lock-form-drill-bit-error" />
            </div>
          </div>
          <div>
            <Label htmlFor="lf-id">Identification</Label>
            <Textarea id="lf-id" rows={3} value={f.identification} onChange={(e) => set("identification")(e.target.value)} maxLength={2000} placeholder="How to recognise this lock: markings, faceplate, keyway…" className="mt-1.5" data-testid="lock-form-identification-input" />
          </div>
          <div>
            <Label htmlFor="lf-dp">Drill point</Label>
            <Textarea id="lf-dp" rows={4} value={f.drill_point} onChange={(e) => set("drill_point")(e.target.value)} maxLength={2000} placeholder="Where to drill, measurements, tips…" className="mt-1.5" data-testid="lock-form-drill-point-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="lock-form-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="lock-form-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{lock ? "Save changes" : "Add lock"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
